import { useEffect, useMemo, useState } from 'react';
import { Column } from '@tanstack/react-table';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';
import { Loader } from 'lucide-react';
import { DateRange } from 'react-day-picker';

import { NormalizedAgreement } from '@/types/file-explorer-types';

import IconAsc from '@/assets/svg/icon-asc.svg?react';
import IconDesc from '@/assets/svg/icon-desc.svg?react';

import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useFiltersStore } from '@/stores/filtersStore';
import { useGlobalStore } from '@/stores/globalStore';
import { useSearchStore } from '@/stores/searchStore';

import {
  COLUMN_NAMES,
  ColumnNamesType,
  ExplorerItemType,
} from '@/constants/table-columns-config';

import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';

interface Props {
  column: Column<NormalizedAgreement>;
  sortBy?: 'asc' | 'desc';
  onSortChange: (sortBy: 'asc' | 'desc') => void;
  onClear: () => void;
  onApply: () => void;
  currentPage: 'search' | 'dashboard';
}

export const FilterWidgetComponent = ({
  column,
  sortBy,
  onSortChange,
  onClear,
  onApply,
  currentPage,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);

  const { searchAgreementFields, getDirectoriesByInsuranceObjects } =
    useSearchStore();
  const { indexes, getAutoFetchUsers, steps } = useGlobalStore();
  const { directories } = useApiManagerStore();

  const [inputValue, setInputValue] = useState<string>('');

  const [options, setOptions] = useState<
    { label: string; value: string | Date }[]
  >([]);

  const { clearFilterByIds, setFilterByIds, getFilterByIds } =
    useFiltersStore();

  const [selectedOptions, setSelectedOptions] = useState<
    Array<{ label: string; value: string | Date }>
  >([]);

  const isDateField = column.id === 'created' || column.id === 'modified';

  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);

  useEffect(() => {
    if (!isDateField) return;
    const dates = getFilterByIds(currentPage)[column.id]?.filterByValues ?? [];
    if (dates.length === 0) {
      setDateRange(undefined);
      setIsLoading(false);
      return;
    }

    const min = dates[0].value;
    const max = dates[1].value;
    if (!min || !max) {
      setDateRange(undefined);
      setIsLoading(false);
      return;
    }
    setDateRange({ from: new Date(min), to: new Date(max) });
    setIsLoading(false);
  }, [isDateField]);

  const handleApply = async () => {
    switch (column.id) {
      case 'created':
      case 'modified':
        const values: Array<{ label: string; value: string | Date }> = [
          {
            label: format(dateRange?.from ?? '', 'dd.MM.yyyy'),
            value: dateRange?.from ?? new Date(),
          },
          {
            label: format(dateRange?.to ?? '', 'dd.MM.yyyy'),
            value: dateRange?.to ?? new Date(),
          },
        ];

        setFilterByIds(
          {
            ...getFilterByIds(currentPage),
            [column.id]: {
              filterCategory: COLUMN_NAMES(ExplorerItemType.AGREEMENT)[
                column.id as ColumnNamesType
              ],
              filterByValues: values,
            },
          },
          currentPage,
        );
        onApply();
        return;

      case 'step':
        setFilterByIds(
          {
            ...getFilterByIds(currentPage),
            [column.id]: {
              filterCategory: COLUMN_NAMES(ExplorerItemType.AGREEMENT)[
                column.id as ColumnNamesType
              ],
              filterByValues: steps
                .filter(
                  (step) =>
                    !step.is_deleted &&
                    selectedOptions.some(
                      (option) => option.value === step.status_name,
                    ),
                )
                .map((step) => ({
                  label: step.status_name,
                  value: step.uuid,
                })),
            },
          },
          currentPage,
        );
        onApply();
        return;

      case 'insurance_object':
        const fields = await searchAgreementFields(currentPage, column.id);
        const insuranceObjFetch =
          await getDirectoriesByInsuranceObjects(fields);
        const selectedInsuranceObjects = insuranceObjFetch.filter((io) =>
          selectedOptions.some((dir) => dir.value === io.directory),
        );
        setFilterByIds(
          {
            ...getFilterByIds(currentPage),
            [column.id]: {
              filterCategory: COLUMN_NAMES(ExplorerItemType.AGREEMENT)[
                column.id as ColumnNamesType
              ],
              filterByValues: selectedInsuranceObjects.map((io) => ({
                label: io.directory,
                value: io.uuid,
              })),
            },
          },
          currentPage,
        );
        onApply();
        return;

      default:
        setFilterByIds(
          {
            ...getFilterByIds(currentPage),
            [column.id]: {
              filterCategory: COLUMN_NAMES(ExplorerItemType.AGREEMENT)[
                column.id as ColumnNamesType
              ],
              filterByValues: selectedOptions,
            },
          },
          currentPage,
        );
        onApply();
        return;
    }
  };

  const handleClear = (field: string) => () => {
    setSelectedOptions([]);
    clearFilterByIds(field, currentPage);
    onClear();
  };

  const handleSortChange = (sortBy: 'asc' | 'desc') => {
    onSortChange(sortBy);
  };

  useEffect(() => {
    setIsLoading(true);

    const fetchFields = async () => {
      const fields = await searchAgreementFields(currentPage, column.id);
      switch (column.id) {
        case 'created':
        case 'modified':
          setOptions([]);
          setSelectedOptions([]);
          break;
        case 'index':
          setOptions(
            indexes
              .filter(
                (index) => !index.is_deleted && fields.includes(index.uuid),
              )
              .map((index) => ({ label: index.name, value: index.uuid })),
          );
          setSelectedOptions(
            getFilterByIds(currentPage)[column.id]?.filterByValues ?? [],
          );
          break;

        case 'creator':
        case 'assignee':
          const newUsers = await getAutoFetchUsers(fields);
          setOptions(
            newUsers
              .filter((user) => fields.includes(user.uuid))
              .map((user) => ({
                label: user.first_name + ' ' + user.last_name,
                value: user.uuid,
              })),
          );
          setSelectedOptions(
            getFilterByIds(currentPage)[column.id]?.filterByValues ?? [],
          );
          break;

        case 'insurance_object':
          const insuranceObjectsFetch =
            await getDirectoriesByInsuranceObjects(fields);

          const directoriesFromInsuranceObjects = [
            ...new Set(
              insuranceObjectsFetch
                .filter((insuranceObject) =>
                  fields.includes(insuranceObject.uuid),
                )
                .map((insuranceObject) => insuranceObject.directory),
            ),
          ];
          const insuranceObjectsSelected = insuranceObjectsFetch
            .filter((io) =>
              getFilterByIds(currentPage)[column.id]?.filterByValues?.some(
                (filter) => (filter.value as string) === io.uuid,
              ),
            )
            .map((io) => io.directory);

          const directoriesSelected = directories.filter((dir) =>
            insuranceObjectsSelected.some((d) => d === dir.uuid),
          );

          setOptions(
            directories
              .filter((dir) =>
                directoriesFromInsuranceObjects.some((d) => d === dir.uuid),
              )
              .map((dir) => ({
                label: dir.abbreviation,
                value: dir.uuid,
              })),
          );

          setSelectedOptions(
            directoriesSelected.map((io) => ({
              label: io.abbreviation,
              value: io.uuid,
            })),
          );

          break;

        case 'step':
          setOptions(
            [
              ...new Set(
                steps
                  .filter(
                    (step) => !step.is_deleted && fields.includes(step.uuid),
                  )
                  .map((step) => step.status_name),
              ),
            ].map((step) => ({
              label: step,
              value: step,
            })),
          );

          const selectedSteps = steps.filter(
            (step) =>
              !step.is_deleted &&
              getFilterByIds(currentPage)[column.id]?.filterByValues?.some(
                (filter) => (filter.value as string) === step.uuid,
              ),
          );

          setSelectedOptions(
            selectedSteps.map((step) => ({
              label: step.status_name,
              value: step.status_name,
            })),
          );
          break;

        default:
          setOptions(fields.map((field) => ({ label: field, value: field })));
          setSelectedOptions(
            getFilterByIds(currentPage)[column.id]?.filterByValues ?? [],
          );
      }

      setIsLoading(false);
    };
    fetchFields();
  }, []);

  useEffect(() => {
    return () => {
      setSelectedOptions([]);
      setInputValue('');
      setDateRange(undefined);
      setIsDatePopoverOpen(false);
      setOptions([]);
    };
  }, []);

  const filterOptions = useMemo(
    () =>
      options
        .filter(
          (option) =>
            !inputValue ||
            option.label.toLowerCase().includes(inputValue.toLowerCase()),
        )
        .sort((a, b) => {
          if (selectedOptions.some((option) => option.value === a.value)) {
            return -1;
          }
          if (selectedOptions.some((option) => option.value === b.value)) {
            return 1;
          }
          return 0;
        }),
    [options, inputValue, selectedOptions],
  );

  const renderContent = () => {
    switch (column.id) {
      case 'created':
      case 'modified':
        return (
          <div className="flex flex-col px-1">
            <Popover
              open={isDatePopoverOpen}
              onOpenChange={setIsDatePopoverOpen}
            >
              <PopoverTrigger asChild>
                <div>
                  <Button
                    variant="outline"
                    className="h-[40px] max-w-none justify-start px-4"
                  >
                    {dateRange?.from
                      ? dateRange?.to
                        ? `${format(dateRange.from, 'dd.MM.yyyy')} - ${format(dateRange.to, 'dd.MM.yyyy')}`
                        : `${format(dateRange.from, 'dd.MM.yyyy')}`
                      : 'Оберіть діапазон дат'}
                  </Button>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  locale={uk}
                  initialFocus
                  numberOfMonths={2}
                />
                <div className="flex items-center justify-end gap-2 border-t p-2">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDateRange(undefined);
                    }}
                  >
                    Очистити
                  </Button>
                  <Button
                    className="h-[32px]"
                    disabled={!dateRange?.from || !dateRange?.to}
                    onClick={() => {
                      handleApply();
                      setIsDatePopoverOpen(false);
                    }}
                  >
                    Застосувати
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        );
      case 'insurance_object':
        return (
          <>
            <div className="flex flex-col px-1">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Параметр фільтру"
              />
            </div>
            <div className="mx-1 flex max-h-[240px] min-h-[80px] flex-col overflow-y-auto rounded-md border border-gray-200 p-4">
              {filterOptions.map((option) => (
                <label
                  key={option.value.toString()}
                  className="flex select-none items-center gap-2 hover:cursor-pointer hover:underline"
                >
                  <Checkbox
                    value={option.value.toString()}
                    onCheckedChange={(checked) => {
                      setSelectedOptions((prev) =>
                        checked
                          ? [...prev, option]
                          : prev.filter((o) => o.value !== option.value),
                      );
                    }}
                    checked={selectedOptions.some(
                      (o) => o.value === option.value,
                    )}
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </>
        );

      case 'step':
        return (
          <>
            <div className="flex flex-col px-1">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Параметр фільтру"
              />
            </div>
            <div className="mx-1 flex max-h-[240px] min-h-[80px] flex-col overflow-y-auto rounded-md border border-gray-200 p-4">
              {filterOptions.map((option) => (
                <label
                  key={option.value.toString()}
                  className="flex select-none items-center gap-2 hover:cursor-pointer hover:underline"
                >
                  <Checkbox
                    value={option.value.toString()}
                    onCheckedChange={(checked) => {
                      setSelectedOptions((prev) =>
                        checked
                          ? [...prev, option]
                          : prev.filter((o) => o.value !== option.value),
                      );
                    }}
                    checked={selectedOptions.some(
                      (o) => o.value === option.value,
                    )}
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </>
        );
      default:
        return (
          <>
            <div className="flex flex-col px-1">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Параметр фільтру"
              />
            </div>
            <div className="mx-1 flex max-h-[240px] min-h-[80px] flex-col overflow-y-auto rounded-md border border-gray-200 p-4">
              {filterOptions.map((option) => (
                <label
                  key={option.value.toString()}
                  className="flex select-none items-center gap-2 hover:cursor-pointer hover:underline"
                >
                  <Checkbox
                    value={option.value.toString()}
                    onCheckedChange={(checked) => {
                      setSelectedOptions((prev) =>
                        checked
                          ? [...prev, option]
                          : prev.filter((o) => o.value !== option.value),
                      );
                    }}
                    checked={selectedOptions.some(
                      (o) => o.value === option.value,
                    )}
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </>
        );
    }
  };

  return (
    <div className="flex flex-col gap-2 px-1 pb-3">
      <div className="flex flex-col gap-2.5 border-b border-separator px-4 pb-2 pt-3">
        <button
          className={`flex items-center gap-1 ${sortBy === 'asc' ? 'font-semibold' : ''}`}
          onClick={() => handleSortChange('asc')}
        >
          <IconAsc className="h-4 w-4" />
          <span className="text-sm">Сортування за зростанням</span>
        </button>
        <button
          className={`flex items-center gap-1 ${sortBy === 'desc' ? 'font-semibold' : ''}`}
          onClick={() => handleSortChange('desc')}
        >
          <IconDesc className="h-4 w-4 rotate-180" />
          <span className="text-sm">Сортування за спаданням</span>
        </button>
      </div>
      {isLoading ? (
        <div className="flex h-16 items-center justify-center">
          <Loader className="h-4 w-4 animate-spin-slow" />
        </div>
      ) : (
        <>{renderContent()}</>
      )}

      <div className="flex justify-between gap-[50px] px-1">
        <Button
          variant="destructive"
          className="h-[40px] w-[120px]"
          onClick={handleClear(column.id)}
        >
          Очистити
        </Button>
        <Button
          variant="outline"
          className="h-[40px] w-[120px]"
          onClick={handleApply}
        >
          Застосувати
        </Button>
      </div>
    </div>
  );
};
